/* ============================================
   Globdrop — Flags UI Renderer
   ============================================ */

import type { FlagGameSession, FlagDifficulty, FlagGameModeType, FlagRoundResult } from '../types/flag.ts';
import { formatPoints } from '../engine/scoring.ts';
import { normalizeText } from '../engine/text.ts';
import { pushAd } from '../utils/ads.ts';
import { track } from '../utils/analytics.ts';
import {
  shareWhatsApp,
  shareFacebook,
  shareInstagram,
  shareNative,
  copyToClipboard,
  canNativeShare,
} from '../engine/share.ts';
import { t, getLocalized } from '../engine/i18n.ts';
import type { ShareData, ScenarioIndex } from '../types/scenario.ts';

// ── Helpers ──

function $(selector: string): HTMLElement {
  return document.querySelector(selector)!;
}

function showToast(message: string): void {
  const toast = $('#toast');
  toast.textContent = message;
  toast.classList.add('visible');
  setTimeout(() => toast.classList.remove('visible'), 2500);
}

function clearMain(): void {
  const main = $('#main');
  main.innerHTML = '';
}

function createScreen(id: string): HTMLElement {
  const div = document.createElement('div');
  div.className = 'screen active';
  div.id = `screen-flag-${id}`;
  return div;
}

/** Get flag image URL based on code and region */
function getFlagImgSrc(code: string, scenarioId: string): string {
  if (scenarioId === 'flags-spain') {
    return `/flags/es/${code}.svg`;
  }
  if (scenarioId === 'flags-fun') {
    return `/flags/fun/${code}.svg`;
  }
  return `https://flagcdn.com/w320/${code.toLowerCase()}.png`;
}

// ── Screen: Difficulty Selection ──

export function renderFlagDifficultySelection(
  meta: { title: any; description: any; icon?: string },
  onSelect: (difficulty: FlagDifficulty) => void,
  onBack: () => void
): void {
  clearMain();
  const screen = createScreen('difficulty');

  screen.innerHTML = `
    <button class="btn-back anim-fade-in-up" id="btn-back">
      <svg viewBox="0 0 24 24" width="20" height="20"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span>${t('mode.back')}</span>
    </button>
    
    <div class="scenario-card__icon" style="font-size: 4rem; margin-bottom: 1rem;">${meta.icon || '🏁'}</div>
    <h1 class="title-section">${getLocalized(meta.title)}</h1>
    <p class="subtitle">${t('flags.choose_difficulty')}</p>
    
    <div class="mode-list stagger" style="max-width: 400px;">
      <button class="btn btn--lg btn--success mode-btn" id="diff-easy">
        <span class="mode-btn__label">${t('flags.difficulty.easy' as any) || 'Fàcil'}</span>
      </button>
      <button class="btn btn--lg btn--info mode-btn" id="diff-medium">
        <span class="mode-btn__label">${t('flags.difficulty.medium' as any) || 'Mitjà'}</span>
      </button>
      <button class="btn btn--lg btn--danger mode-btn" id="diff-hard">
        <span class="mode-btn__label">${t('flags.difficulty.hard' as any) || 'Difícil'}</span>
      </button>
    </div>
  `;

  $('#main').appendChild(screen);

  $('#btn-back').addEventListener('click', onBack);

  screen.querySelector('#diff-easy')!.addEventListener('click', () => onSelect('easy'));
  screen.querySelector('#diff-medium')!.addEventListener('click', () => onSelect('medium'));
  screen.querySelector('#diff-hard')!.addEventListener('click', () => onSelect('hard'));
}

// ── Screen: Mode Selection ──

export function renderFlagGameModeSelection(
  meta: { title: any; description: any; icon?: string },
  difficulty: FlagDifficulty,
  onSelect: (mode: FlagGameModeType) => void,
  onBack: () => void
): void {
  clearMain();
  const screen = createScreen('mode');

  let diffLabel = '';
  if (difficulty === 'easy') diffLabel = t('flags.difficulty.easy' as any) || 'Fàcil';
  else if (difficulty === 'medium') diffLabel = t('flags.difficulty.medium' as any) || 'Mitjà';
  else diffLabel = t('flags.difficulty.hard' as any) || 'Difícil';

  screen.innerHTML = `
    <button class="btn-back anim-fade-in-up" id="btn-back">
      <svg viewBox="0 0 24 24" width="20" height="20"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span>${t('mode.back')}</span>
    </button>
    
    <div class="scenario-card__icon" style="font-size: 4rem; margin-bottom: 1rem;">${meta.icon || '🏁'}</div>
    <h1 class="title-section">${getLocalized(meta.title)} (${diffLabel})</h1>
    <p class="subtitle">${t('flags.choose_mode')}</p>
    
    <div class="mode-list stagger" style="max-width: 400px;">
      <button class="btn btn--lg btn--success mode-btn" id="mode-test">
        <span class="mode-btn__label">🏁 ${t('flags.mode.test' as any) || 'Tipo Test'}</span>
      </button>
      <button class="btn btn--lg btn--info mode-btn" id="mode-write">
        <span class="mode-btn__label">✏️ ${t('flags.mode.write' as any) || 'Escriure Nom'}</span>
      </button>
    </div>
  `;

  $('#main').appendChild(screen);

  $('#btn-back').addEventListener('click', onBack);

  screen.querySelector('#mode-test')!.addEventListener('click', () => onSelect('test'));
  screen.querySelector('#mode-write')!.addEventListener('click', () => onSelect('write'));
}

// ── Screen: Countdown ──

export function renderFlagCountdown(
  session: FlagGameSession,
  onDone: () => void
): void {
  clearMain();
  const screen = createScreen('countdown');

  screen.innerHTML = `
    <p class="round-indicator">${session.currentRound + 1} ${t('game.round')} ${session.totalRounds}</p>
    <p class="place-name" id="place-name" style="visibility: hidden;">Flag</p>
    <div class="countdown-number" id="countdown-number">3</div>
  `;

  $('#main').appendChild(screen);

  const numEl = screen.querySelector('#countdown-number') as HTMLElement;
  let count = 3;

  const interval = setInterval(() => {
    count--;
    if (count <= 0) {
      clearInterval(interval);
      onDone();
    } else {
      numEl.textContent = String(count);
      numEl.classList.remove('anim-countdown-tick');
      void numEl.offsetWidth; // Force reflow to restart animation
      numEl.classList.add('anim-countdown-tick');
    }
  }, 1000);
}

// ── Screen: Playing ──

export function renderFlagPlaying(
  session: FlagGameSession,
  autocompletePool: string[],
  onChoiceSelect: (code: string) => void,
  onTextSubmit: (text: string) => void,
  onTimerTick: (updateFn: (remaining: number) => void) => void
): void {
  clearMain();
  const screen = createScreen('playing');
  screen.classList.add('screen--full');

  const flagUrl = getFlagImgSrc(session.currentQuestion!.code, session.scenarioId);

  screen.innerHTML = `
    <div class="playing-wrapper">
      <div class="ad-sidebar ad-sidebar-left ad-container ad-stack">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>
      <div class="game-container stagger">
        <!-- HUD -->
        <div class="game-hud" id="hud">
          <div class="hud-left">
            <span class="hud-points" id="hud-points">${formatPoints(session.totalPoints)} ${t('game.points')}</span>
          </div>
          <div class="hud-center">
            <span class="hud-place" id="hud-place">${session.currentRound + 1} / ${session.totalRounds}</span>
          </div>
          <div class="hud-right">
            <div class="hud-timer" id="hud-timer">
              <svg class="timer-ring" viewBox="0 0 48 48">
                <circle class="timer-ring__bg" cx="24" cy="24" r="20" />
                <circle class="timer-ring__fg" id="timer-ring-fg" cx="24" cy="24" r="20" />
              </svg>
              <span class="timer-text" id="timer-text">${session.timeRemaining}</span>
            </div>
          </div>
        </div>

        <!-- Flag Question Display -->
        <div class="flag-question-container">
          <div class="flag-image-wrapper">
            <img src="${flagUrl}" alt="${t('flags.flag_alt')}" class="flag-image anim-scale-in" draggable="false" />
          </div>
        </div>

        <!-- Input Modes -->
        <div id="flag-interactive-area"></div>
      </div>
      <div class="ad-sidebar ad-sidebar-right ad-container ad-stack">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>
    </div>
  `;

  $('#main').appendChild(screen);
  for (let i = 0; i < 4; i++) {
    pushAd();
  }

  const interactiveArea = screen.querySelector('#flag-interactive-area')!;

  // Render Multiple Choice Options
  if (session.mode === 'test') {
    const grid = document.createElement('div');
    grid.className = 'flag-options-grid';
    session.options.forEach(opt => {
      const btn = document.createElement('button');
      btn.className = 'flag-option-btn';
      btn.setAttribute('data-code', opt.code);
      btn.textContent = getLocalized(opt.name);
      btn.addEventListener('click', () => {
        onChoiceSelect(opt.code);
      });
      grid.appendChild(btn);
    });
    interactiveArea.appendChild(grid);
  } 
  // Render Write Name Form with custom autocomplete dropdown
  else {
    const container = document.createElement('div');
    container.className = 'flag-write-container';

    container.innerHTML = `
      <div class="flag-input-wrapper">
        <input
          type="text"
          class="flag-input"
          id="flag-text-input"
          placeholder="${t('flags.placeholder' as any)}"
          autocomplete="off"
          autofocus
        />
        <button class="btn btn--primary flag-submit-btn" id="btn-submit-answer">
          ${t('flags.submit' as any)}
        </button>
      </div>
      <ul class="flag-autocomplete-dropdown hidden" id="autocomplete-list"></ul>
    `;

    interactiveArea.appendChild(container);

    const input = container.querySelector('#flag-text-input') as HTMLInputElement;
    const submitBtn = container.querySelector('#btn-submit-answer') as HTMLButtonElement;
    const list = container.querySelector('#autocomplete-list') as HTMLUListElement;

    let highlightedIndex = -1;
    let suggestions: string[] = [];

    // Filter autocomplete pool
    const updateSuggestions = () => {
      const val = input.value;
      list.innerHTML = '';
      highlightedIndex = -1;

      if (!val) {
        list.classList.add('hidden');
        return;
      }

      const normQuery = normalizeText(val);
      suggestions = autocompletePool.filter(name => 
        normalizeText(name).includes(normQuery)
      ).slice(0, 5); // Limit to 5 suggestions

      if (suggestions.length === 0) {
        list.innerHTML = `<li class="flag-autocomplete-item--empty">${t('flags.autocomplete.no_results' as any)}</li>`;
        list.classList.remove('hidden');
        return;
      }

      suggestions.forEach((sug) => {
        const li = document.createElement('li');
        li.className = 'flag-autocomplete-item';
        li.textContent = sug;
        li.addEventListener('click', () => {
          input.value = sug;
          list.classList.add('hidden');
          input.focus();
        });
        list.appendChild(li);
      });
      list.classList.remove('hidden');
    };

    input.addEventListener('input', updateSuggestions);

    // Keyboard navigation in custom autocomplete list
    input.addEventListener('keydown', (e: KeyboardEvent) => {
      const items = list.querySelectorAll('.flag-autocomplete-item');

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (items.length > 0) {
          highlightedIndex = (highlightedIndex + 1) % items.length;
          items.forEach((item, idx) => {
            if (idx === highlightedIndex) {
              item.classList.add('active');
              input.value = suggestions[idx]!;
            } else {
              item.classList.remove('active');
            }
          });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (items.length > 0) {
          highlightedIndex = (highlightedIndex - 1 + items.length) % items.length;
          items.forEach((item, idx) => {
            if (idx === highlightedIndex) {
              item.classList.add('active');
              input.value = suggestions[idx]!;
            } else {
              item.classList.remove('active');
            }
          });
        }
      } else if (e.key === 'Enter') {
        // Enter confirms a word in a Chinese or Japanese input method; don't submit then
        if (e.isComposing || e.keyCode === 229) return;
        e.preventDefault();
        list.classList.add('hidden');
        submitBtn.click();
      } else if (e.key === 'Escape') {
        list.classList.add('hidden');
      }
    });

    // Close autocomplete when clicking outside
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target as Node)) {
        list.classList.add('hidden');
      }
    });

    submitBtn.addEventListener('click', () => {
      list.classList.add('hidden');
      onTextSubmit(input.value);
    });
  }

  // ── Timer logic ──
  const circumference = 2 * Math.PI * 20;
  const ringFg = screen.querySelector('#timer-ring-fg') as SVGCircleElement;
  ringFg.style.strokeDasharray = String(circumference);
  ringFg.style.strokeDashoffset = '0';

  const timerText = screen.querySelector('#timer-text') as HTMLElement;
  const maxTime = 15;

  onTimerTick((remaining: number) => {
    timerText.textContent = String(Math.ceil(remaining));
    const progress = 1 - remaining / maxTime;
    ringFg.style.strokeDashoffset = String(circumference * progress);

    if (remaining <= 4) {
      ringFg.style.stroke = 'var(--color-danger)';
      timerText.style.color = 'var(--color-danger)';
    }
  });
}

// ── Screen: Feedback ──

export function renderFlagFeedback(
  result: FlagRoundResult,
  session: FlagGameSession,
  onNext: () => void
): void {
  // Update HUD score
  const hudPoints = document.querySelector('#hud-points');
  if (hudPoints) {
    hudPoints.textContent = `${formatPoints(session.totalPoints)} ${t('game.points')}`;
  }

  const container = $('.game-container');

  // Disable Inputs and Highlight Answers
  if (session.mode === 'test') {
    const buttons = document.querySelectorAll('.flag-option-btn');
    buttons.forEach((btn: any) => {
      btn.disabled = true;
      const code = btn.getAttribute('data-code');
      if (code === result.flag.code) {
        btn.classList.add('correct');
      } else if (code === result.userAnswer && !result.correct) {
        btn.classList.add('incorrect');
      }
    });
  } else {
    // Disable text box
    const input = document.querySelector('#flag-text-input') as HTMLInputElement;
    const submitBtn = document.querySelector('#btn-submit-answer') as HTMLButtonElement;
    if (input) input.disabled = true;
    if (submitBtn) submitBtn.disabled = true;

    // Renders feedback message
    const msg = document.createElement('div');
    if (result.correct) {
      msg.className = 'flag-feedback-msg correct';
      msg.textContent = t('flags.feedback.correct' as any);
    } else {
      msg.className = 'flag-feedback-msg incorrect';
      const flagName = getLocalized(result.flag.name);
      if (result.userAnswer === '') {
        msg.textContent = `${t('flags.feedback.timeout' as any)}: ${flagName}`;
      } else {
        msg.textContent = `${t('flags.feedback.incorrect' as any)} ${flagName}`;
      }
    }
    $('.flag-write-container').appendChild(msg);
  }

  // Next Button
  const nextBtn = document.createElement('button');
  nextBtn.className = 'btn btn--primary btn--lg next-btn anim-fade-in-up';
  nextBtn.id = 'btn-next';
  nextBtn.style.alignSelf = 'center';
  nextBtn.style.marginTop = '2rem';
  nextBtn.innerHTML = `
    <span>+${formatPoints(result.points)} ${t('game.points').toLowerCase()}</span>
    <span class="next-btn__arrow">→ ${t('game.next')}</span>
  `;
  nextBtn.addEventListener('click', onNext);
  container.appendChild(nextBtn);
}

// ── Screen: Results ──

export function renderFlagResults(
  session: FlagGameSession,
  index: ScenarioIndex,
  onReplay: () => void,
  onHome: () => void
): void {
  clearMain();
  const screen = createScreen('results');

  const maxPts = session.totalRounds * 200; // Max points per round is 200
  const ratio = session.totalPoints / maxPts;

  // Custom levels for Flags Game
  let level = '';
  if (ratio < 0.25) {
    level = t('flags.results.level.novice' as any);
  } else if (ratio < 0.6) {
    level = t('flags.results.level.amateur' as any);
  } else if (ratio < 0.85) {
    level = t('flags.results.level.expert' as any);
  } else {
    level = t('flags.results.level.master' as any);
  }

  const scenarioMeta = index.scenarios.find(s => s.id === session.scenarioId);
  const scenarioName = scenarioMeta ? getLocalized(scenarioMeta.title) : t('tabs.flags');

  const shareData: ShareData = {
    scenario: scenarioName,
    mode: session.mode === 'test' ? t('flags.mode.test') : t('flags.mode.write'),
    score: session.totalPoints,
    level: level,
    kmTotal: 0,
    appUrl: window.location.origin,
    isFlags: true,
    correctCount: session.rounds.filter(r => r.correct).length,
    totalRounds: session.totalRounds,
  };

  screen.innerHTML = `
    <div class="results stagger">
      <div class="results__score anim-scale-in">
        <span class="results__emoji">🏆</span>
        <h1 class="results__points">${formatPoints(session.totalPoints)} ${t('results.title')}</h1>
        <p class="results__level">${t('results.level_prefix')} <strong>${level}</strong></p>
      </div>

      <!-- Details of all rounds played -->
      <div class="flag-results-list anim-fade-in-up">
        ${session.rounds.map((r, index) => {
          const flagUrl = getFlagImgSrc(r.flag.code, session.scenarioId);
          return `
            <div class="flag-result-row">
              <div class="flag-result-row__left">
                <span style="color: var(--color-text-muted); font-family: var(--font-family-mono); font-size: 0.8rem;">#${index+1}</span>
                <img src="${flagUrl}" class="flag-result-row__flag" />
                <span class="flag-result-row__name">${getLocalized(r.flag.name)}</span>
              </div>
              <div class="flag-result-row__right">
                <span class="flag-result-row__points">+${r.points}</span>
                <span class="flag-result-row__badge ${r.correct ? 'flag-result-row__badge--correct' : 'flag-result-row__badge--incorrect'}">
                  ${r.correct ? '✓' : '✗'}
                </span>
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <!-- Sharing controls -->
      <div class="results__share">
        ${canNativeShare() ? `
          <button class="btn btn--primary btn--lg" id="btn-share-native">
            📤 ${t('results.share')}
          </button>
        ` : ''}

        <div class="share-buttons">
          <button class="btn btn--icon share-btn share-btn--wa" id="btn-share-wa" title="WhatsApp">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--fb" id="btn-share-fb" title="Facebook">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--ig" id="btn-share-ig" title="Instagram">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--copy" id="btn-share-copy" title="${t('results.copy')}" aria-label="${t('results.copy')}">
            📋
          </button>
        </div>
      </div>

      <div class="results__actions">
        <button class="btn btn--secondary btn--lg" id="btn-replay">🔄 ${t('results.replay')}</button>
        <button class="btn btn--secondary btn--lg" id="btn-home">🏠 ${t('results.home')}</button>
      </div>
      
      <div class="ad-container" style="margin-top: 2rem; width: 100%; text-align: center;">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>
    </div>
  `;

  $('#main').appendChild(screen);
  pushAd();

  // Wire up share buttons (un solo evento `share` por clic, sea cual sea el boton)
  screen.querySelector('.results__share')?.addEventListener('click', (e) => {
    if ((e.target as Element).closest('button')) {
      track('share', { mode: 'flags', map: session.scenarioId });
    }
  });
  const btnWa = screen.querySelector('#btn-share-wa');
  btnWa?.addEventListener('click', () => {
    shareWhatsApp(shareData);
    showToast(t('results.share_wa'));
  });

  const btnFb = screen.querySelector('#btn-share-fb');
  btnFb?.addEventListener('click', () => {
    shareFacebook(shareData);
    showToast(t('results.share_fb'));
  });

  const btnIg = screen.querySelector('#btn-share-ig');
  btnIg?.addEventListener('click', async () => {
    await shareInstagram(shareData);
    showToast(t('results.share_ig'));
  });

  const btnCopy = screen.querySelector('#btn-share-copy');
  btnCopy?.addEventListener('click', async () => {
    await copyToClipboard(shareData);
    showToast(t('results.share_copy'));
  });

  const btnNative = screen.querySelector('#btn-share-native');
  btnNative?.addEventListener('click', async () => {
    const shared = await shareNative(shareData);
    if (!shared) {
      await copyToClipboard(shareData);
      showToast(t('results.share_copy'));
    }
  });

  // Wire up action buttons
  screen.querySelector('#btn-replay')?.addEventListener('click', onReplay);
  screen.querySelector('#btn-home')?.addEventListener('click', onHome);
}
